import {todayYmd} from '../helpers/date';

/**
 * Conversions between what the approval forms hold as text and what the API
 * takes. Shared by the completion form, the requester's form and the manager's
 * work-up form, so none of them may own them.
 */

/** date inputs need `yyyy-MM-dd`; the API sends dates as UTC midnight, so read them as UTC */
export const toDateInput = (v?: Date | null) => v ? v.toISOString().substr(0, 10) : '';

/** the reverse: a plain `yyyy-MM-dd` string parses as UTC midnight and round-trips unchanged */
export const fromDateInput = (v: string) => v ? new Date(v) : undefined;

/** the API sends `HH:MM:SS`, the native time input takes `HH:MM` */
export const toTimeInput = (v?: string | null) => v ? v.substr(0, 5) : '';

export const toPrice = (v: string): number | undefined => {
    const parsed = parseFloat(v.replace(/\s/g, '').replace(',', '.'));
    return isNaN(parsed) ? undefined : parsed;
};

/**
 * Mirrors what the model accepts: a non-negative amount with at most two decimal
 * places (`price` numericality plus `price_has_at_most_two_decimal_places`).
 * Matched on the raw text so 1,234 is rejected rather than silently truncated.
 */
export const isPriceValid = (v: string) => /^\d+([.,]\d{1,2})?$/.test(v.replace(/\s/g, ''));

/**
 * The API insists on `HH:MM` with at least two digits for hours
 * (ApprovalRequests::Create::STUDY_TIME_FORMAT), so accept the shorthand people
 * actually type and pad it out. Returns undefined when it cannot be understood.
 */
export const toStudyTime = (v: string): string | undefined => {
    const trimmed = v.trim();
    if (!trimmed) {
        return undefined;
    }
    const match = trimmed.match(/^(\d{1,4})(?:[:.,](\d{1,2}))?$/);
    if (!match) {
        return undefined;
    }
    const minutes = match[2] ? match[2].padEnd(2, '0') : '00';
    if (Number(minutes) > 59) {
        return undefined;
    }
    return match[1].padStart(2, '0') + ':' + minutes;
};

/** The same value the API derives, so the form can refuse a zero duration. */
export const toStudyTimeMinutes = (v: string): number | undefined => {
    const value = toStudyTime(v);
    if (!value) {
        return undefined;
    }
    const [hours, minutes] = value.split(':');
    return Number(hours) * 60 + Number(minutes);
};

export interface RequestTermValues {
    subject_date: string;
    subject_end_date: string;
    subject_start_time: string;
    subject_end_time: string;
}

/**
 * The term rules a pending request obeys: `draft_date_cannot_be_in_past`,
 * `draft_date_range`, `draft_time_range` and `draft_time_cannot_be_in_past`.
 * Checked up front so the message lands on the field instead of coming back as
 * a server error.
 */
export const validateRequestTerm = (values: RequestTermValues, t: (key: string) => string) => {
    const errors: Partial<Record<keyof RequestTermValues, string>> = {};
    const today = todayYmd();

    if (values.subject_date && values.subject_date < today) {
        errors.subject_date = t('approvals.form.subjectDateInPast');
    }
    if (values.subject_end_date && !values.subject_date) {
        errors.subject_date = t('form.required');
    }
    if (values.subject_date && values.subject_end_date && values.subject_end_date < values.subject_date) {
        errors.subject_end_date = t('approvals.form.endDateBeforeSubjectDate');
    }
    if (values.subject_end_time && !values.subject_start_time) {
        errors.subject_start_time = t('form.required');
    }
    // only compared within a single day, the same way draft_time_range does it
    if (values.subject_start_time && values.subject_end_time
        && values.subject_date && values.subject_end_date
        && values.subject_date === values.subject_end_date
        && values.subject_end_time < values.subject_start_time) {
        errors.subject_end_time = t('approvals.form.endTimeBeforeStartTime');
    }
    // a course starting today cannot have started already
    if (values.subject_start_time && values.subject_date === today
        && values.subject_start_time <= new Date().toTimeString().slice(0, 5)) {
        errors.subject_start_time = t('approvals.form.startTimeInPast');
    }
    return errors;
};
